@file:OptIn(ExperimentalMaterial3Api::class)

package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.SocialMediaAccount
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.ui.media.rememberMediaPickerState
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.AccountsStore
import pl.zar.tikthrow.viewmodel.AccountsViewModel
import pl.zar.tikthrow.viewmodel.ContentType
import pl.zar.tikthrow.viewmodel.QueueViewModel
import kotlin.math.roundToInt

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

private val platformColors = mapOf(
    Platform.TWITTER   to Color(0xFF1D9BF0),
    Platform.INSTAGRAM to Color(0xFFE1306C),
    Platform.FACEBOOK  to Color(0xFF1877F2),
    Platform.TIKTOK    to Color(0xFFFF0050),
    Platform.YOUTUBE   to Color(0xFFFF0000),
    Platform.DISCORD   to Color(0xFF5865F2),
)

@Composable
fun AddQueuedPostScreen(
    viewModel: QueueViewModel,
    onBack: () -> Unit,
) {
    // Platforms auto-detect Shorts vs. regular video from the file itself, so there's nothing to
    // choose here. Photo/text posts (ContentType.POST) get their own dedicated form later.
    val selectedType = ContentType.VIDEO
    var media by remember { mutableStateOf(emptyList<MediaItem>()) }
    var selectedPlatforms by remember { mutableStateOf(emptySet<Platform>()) }
    var error by remember { mutableStateOf<String?>(null) }
    // Title/caption live per file, keyed by MediaItem.uri — set by tapping a file, not through a
    // shared form field. A file with no entry here is still blank.
    var perItemText by remember { mutableStateOf(mapOf<String, Pair<String, String>>()) }
    var editingItem by remember { mutableStateOf<MediaItem?>(null) }

    val accountsViewModel: AccountsViewModel = viewModel { AccountsViewModel() }
    LaunchedEffect(Unit) {
        if (AccountsStore.accounts.value.none { it.platform == Platform.YOUTUBE }) {
            accountsViewModel.refreshYouTubeStatus()
        }
        if (AccountsStore.accounts.value.none { it.platform == Platform.TIKTOK }) {
            accountsViewModel.refreshTikTokStatus()
        }
    }
    val allAccounts by AccountsStore.accounts.collectAsState()
    val youtubeAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.YOUTUBE } }
    val tiktokAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.TIKTOK } }
    var selectedYouTubeAccountId by remember { mutableStateOf<String?>(null) }
    var selectedTikTokAccountId by remember { mutableStateOf<String?>(null) }

    val picker = rememberMediaPickerState(contentType = selectedType) { newMedia -> media = media + newMedia }

    val canSave = media.isNotEmpty() && selectedPlatforms.isNotEmpty()

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Column(modifier = Modifier.fillMaxSize()) {
            Surface(color = Slate100, shadowElevation = 2.dp) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .statusBarsPadding()
                        .padding(horizontal = 8.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Wróć", tint = Slate800)
                    }
                    Column {
                        Text(
                            text = "Dodaj do kolejki",
                            style = TextStyle(brush = gradientBrush, fontSize = 20.sp, fontWeight = FontWeight.ExtraBold)
                        )
                        Text(
                            text = if (media.size > 1) "${media.size} postów trafi na koniec kolejki — po jednym na plik"
                                   else "Post trafi na koniec kolejki",
                            fontSize = 12.sp,
                            color = Slate500
                        )
                    }
                }
            }

            Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                    .widthIn(max = 480.dp)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                // Media
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("MEDIA", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                    if (media.isEmpty()) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(160.dp)
                                .clip(RoundedCornerShape(6.dp))
                                .background(Slate100)
                                .border(2.dp, Slate300, RoundedCornerShape(6.dp))
                                .clickable { picker.launch() },
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                Box(
                                    modifier = Modifier.size(44.dp).clip(RoundedCornerShape(4.dp)).background(gradientBrush),
                                    contentAlignment = Alignment.Center
                                ) { Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(22.dp), tint = Color.White) }
                                Text(
                                    "Dodaj wideo",
                                    fontSize = 14.sp, fontWeight = FontWeight.SemiBold, color = Slate500
                                )
                                Text(
                                    "Możesz zaznaczyć kilka naraz — każdy trafi jako osobny post w kolejce",
                                    fontSize = 11.sp, color = Slate400, textAlign = TextAlign.Center,
                                    modifier = Modifier.padding(horizontal = 20.dp)
                                )
                            }
                        }
                    } else {
                        Text(
                            "Dotknij pliku, aby ustawić jego tytuł i opis.",
                            fontSize = 11.sp, color = Slate500
                        )
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            media.forEach { item ->
                                MediaDraftRow(
                                    item = item,
                                    override = perItemText[item.uri],
                                    onEdit = { editingItem = item },
                                    onRemove = {
                                        media = media - item
                                        perItemText = perItemText - item.uri
                                    },
                                )
                            }
                        }
                        Surface(
                            onClick = { picker.launch() },
                            shape = RoundedCornerShape(6.dp),
                            color = Slate100,
                            border = BorderStroke(1.5.dp, Slate300),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(vertical = 10.dp),
                                horizontalArrangement = Arrangement.Center,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp), tint = Slate600)
                                Spacer(Modifier.width(6.dp))
                                Text("Dodaj więcej plików", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Slate600)
                            }
                        }
                        val totalSize = media.sumOf { it.fileSizeBytes ?: 0L }
                        if (totalSize > 0L) Text("${media.size} plik${if (media.size == 1) "" else "ów"} · ${formatQueueFileSize(totalSize)}", fontSize = 12.sp, color = Slate500)
                    }
                }

                // Platforms
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("PUBLIKUJ NA", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Platform.entries.forEach { platform ->
                            val isSelected = platform in selectedPlatforms
                            val color = platformColors[platform] ?: Slate600
                            Surface(
                                onClick = {
                                    selectedPlatforms = if (isSelected) selectedPlatforms - platform else selectedPlatforms + platform
                                    error = null
                                },
                                shape = RoundedCornerShape(6.dp),
                                color = if (isSelected) Slate200 else Slate100,
                                shadowElevation = if (isSelected) 0.dp else 1.dp,
                                border = if (isSelected) BorderStroke(1.5.dp, Slate600) else BorderStroke(1.dp, Slate200)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                        Box(modifier = Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(color))
                                        Text(
                                            text = platform.displayName,
                                            fontSize = 15.sp,
                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                            color = if (isSelected) Slate900 else Slate800
                                        )
                                    }
                                    Checkbox(
                                        checked = isSelected,
                                        onCheckedChange = { selectedPlatforms = if (isSelected) selectedPlatforms - platform else selectedPlatforms + platform },
                                        colors = CheckboxDefaults.colors(checkedColor = Slate800, uncheckedColor = Slate300, checkmarkColor = Slate50)
                                    )
                                }
                            }
                            if (platform == Platform.YOUTUBE && isSelected && youtubeAccounts.isNotEmpty()) {
                                QueueAccountPickerRow(
                                    accounts = youtubeAccounts,
                                    selectedId = selectedYouTubeAccountId,
                                    accentColor = Color(0xFFFF0000),
                                    onSelect = { selectedYouTubeAccountId = it },
                                )
                            }
                            if (platform == Platform.TIKTOK && isSelected && tiktokAccounts.isNotEmpty()) {
                                QueueAccountPickerRow(
                                    accounts = tiktokAccounts,
                                    selectedId = selectedTikTokAccountId,
                                    accentColor = Color(0xFFFF0050),
                                    onSelect = { selectedTikTokAccountId = it },
                                )
                            }
                        }
                    }
                }

                error?.let {
                    Text(it, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = MaterialTheme.colorScheme.error)
                }

                Spacer(Modifier.height(8.dp))
            }
            }

            // Bottom bar
            Surface(color = Slate100, shadowElevation = 8.dp) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .navigationBarsPadding()
                        .padding(horizontal = 16.dp, vertical = 14.dp)
                        .height(52.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(if (canSave) gradientBrush else Brush.linearGradient(listOf(Slate200, Slate200)))
                        .clickable(enabled = canSave) {
                            if (media.isEmpty()) {
                                error = "Dodaj przynajmniej jeden plik"
                                return@clickable
                            }
                            if (selectedPlatforms.isEmpty()) {
                                error = "Wybierz przynajmniej jedną platformę"
                                return@clickable
                            }
                            val effectiveYtId = selectedYouTubeAccountId ?: youtubeAccounts.firstOrNull()?.id
                            val effectiveTtId = selectedTikTokAccountId ?: tiktokAccounts.firstOrNull()?.id
                            // Each publish only ever sends one file, so N files become N separate
                            // queue entries — each using the title/caption set by tapping that file
                            // (blank if it was never opened).
                            media.forEach { item ->
                                val text = perItemText[item.uri]
                                viewModel.addPost(
                                    type = selectedType,
                                    media = listOf(item),
                                    title = text?.first ?: "",
                                    caption = text?.second ?: "",
                                    platforms = selectedPlatforms,
                                    youtubeAccountId = if (Platform.YOUTUBE in selectedPlatforms) effectiveYtId else null,
                                    tiktokAccountId = if (Platform.TIKTOK in selectedPlatforms) effectiveTtId else null,
                                )
                            }
                            onBack()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = if (media.size > 1) "Dodaj ${media.size} do kolejki" else "Dodaj do kolejki",
                        color = if (canSave) Color.White else Slate400,
                        fontSize = 15.sp,
                        fontWeight = FontWeight.ExtraBold,
                        letterSpacing = (-0.3).sp
                    )
                }
            }
        }
    }

    editingItem?.let { item ->
        MediaItemEditDialog(
            item = item,
            currentText = perItemText[item.uri],
            onDismiss = { editingItem = null },
            onSave = { newTitle, newCaption ->
                perItemText = perItemText + (item.uri to (newTitle to newCaption))
                editingItem = null
            },
            onClear = {
                perItemText = perItemText - item.uri
                editingItem = null
            },
        )
    }
}

/**
 * One file waiting for its title/caption: tap anywhere on the row to set them. A red prompt
 * marks files that still need attention; a green check confirms a file is ready to publish.
 */
@Composable
private fun MediaDraftRow(
    item: MediaItem,
    override: Pair<String, String>?,
    onEdit: () -> Unit,
    onRemove: () -> Unit,
) {
    val rawTitle = override?.first ?: ""
    val rawCaption = override?.second ?: ""
    val isComplete = rawTitle.isNotBlank() && rawCaption.isNotBlank()
    Surface(
        onClick = onEdit,
        shape = RoundedCornerShape(8.dp),
        color = if (isComplete) Slate100 else Rose500.copy(alpha = 0.06f),
        border = if (isComplete) null else BorderStroke(1.dp, Rose500.copy(alpha = 0.35f)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Box(
                modifier = Modifier.size(48.dp).clip(RoundedCornerShape(4.dp)).background(Slate200)
            ) {
                ImageFromUri(uri = item.uri, isVideo = item.isVideo, modifier = Modifier.matchParentSize())
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = item.fileName ?: "Plik",
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Slate900,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (isComplete) {
                    Text(
                        text = rawTitle,
                        fontSize = 11.sp,
                        color = Slate500,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                } else {
                    Text(
                        text = "Uzupełnij tytuł i opis",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = Rose500,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            if (isComplete) {
                Icon(
                    Icons.Default.CheckCircle,
                    contentDescription = "Tytuł i opis uzupełnione",
                    tint = Emerald500,
                    modifier = Modifier.size(20.dp)
                )
            }
            IconButton(onClick = onRemove, modifier = Modifier.size(20.dp)) {
                Icon(Icons.Default.Close, contentDescription = "Usuń plik", tint = Slate500, modifier = Modifier.size(14.dp))
            }
        }
    }
}

/** Sets the title/caption a single queued file will publish with. */
@Composable
private fun MediaItemEditDialog(
    item: MediaItem,
    currentText: Pair<String, String>?,
    onDismiss: () -> Unit,
    onSave: (title: String, caption: String) -> Unit,
    onClear: () -> Unit,
) {
    var itemTitle by remember(item.uri) { mutableStateOf(currentText?.first ?: "") }
    var itemCaption by remember(item.uri) { mutableStateOf(currentText?.second ?: "") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Slate100,
        title = {
            Text(
                text = item.fileName ?: "Plik",
                fontSize = 15.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Slate900,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = itemTitle,
                    onValueChange = { if (it.length <= 100) itemTitle = it },
                    label = { Text("Tytuł") },
                    placeholder = { Text("Tytuł (np. dla YouTube)", color = Slate400) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Slate600, unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900, unfocusedTextColor = Slate900,
                        cursorColor = Slate800,
                        focusedContainerColor = Slate50, unfocusedContainerColor = Slate50,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
                OutlinedTextField(
                    value = itemCaption,
                    onValueChange = { itemCaption = it },
                    label = { Text("Opis") },
                    placeholder = { Text("Opis publikacji (YouTube / TikTok)", color = Slate400) },
                    modifier = Modifier.fillMaxWidth().heightIn(min = 100.dp),
                    maxLines = 6,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Slate600, unfocusedBorderColor = Slate200,
                        focusedTextColor = Slate900, unfocusedTextColor = Slate900,
                        cursorColor = Slate800,
                        focusedContainerColor = Slate50, unfocusedContainerColor = Slate50,
                    ),
                    shape = RoundedCornerShape(6.dp)
                )
                if (currentText != null) {
                    Text(
                        text = "Wyczyść tytuł i opis",
                        fontSize = 11.sp,
                        color = Slate500,
                        modifier = Modifier.clickable { onClear() }
                    )
                }
            }
        },
        confirmButton = {
            TextButton(onClick = { onSave(itemTitle, itemCaption) }) {
                Text("Zapisz", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 15.sp)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Anuluj", color = Slate500) }
        },
        shape = RoundedCornerShape(8.dp)
    )
}

@Composable
private fun QueueAccountPickerRow(
    accounts: List<SocialMediaAccount>,
    selectedId: String?,
    accentColor: Color,
    onSelect: (String) -> Unit,
) {
    Column(
        modifier = Modifier.padding(start = 12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        accounts.forEach { account ->
            val isSelected = account.id == selectedId || (selectedId == null && account == accounts.first())
            Surface(
                onClick = { onSelect(account.id) },
                shape = RoundedCornerShape(4.dp),
                color = if (isSelected) accentColor.copy(alpha = 0.10f) else Slate100,
                border = if (isSelected) BorderStroke(1.5.dp, accentColor.copy(0.35f)) else BorderStroke(1.dp, Slate200),
                shadowElevation = if (isSelected) 0.dp else 1.dp,
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = account.displayName ?: account.accountHandle,
                            fontSize = 14.sp,
                            fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                            color = if (isSelected) accentColor else Slate600,
                        )
                        if (account.displayName != null) {
                            Text(text = account.accountHandle, fontSize = 11.sp, color = Slate500)
                        }
                    }
                    RadioButton(
                        selected = isSelected,
                        onClick = { onSelect(account.id) },
                        colors = RadioButtonDefaults.colors(selectedColor = accentColor, unselectedColor = Slate300),
                    )
                }
            }
        }
    }
}

private fun formatQueueFileSize(bytes: Long): String {
    return when {
        bytes >= 1024L * 1024L * 1024L -> "${((bytes / (1024.0 * 1024.0 * 1024.0)) * 10).roundToInt() / 10.0} GB"
        bytes >= 1024L * 1024L -> "${(bytes / (1024.0 * 1024.0)).roundToInt()} MB"
        else -> "${bytes / 1024} KB"
    }
}
